// Function index:
//   ACTIONS — the five buttons, in the order they are shown.
//   mountActionBar(host, ws, getTargetNpcId?, getTargetCharacterId?, archetype?, say?) —
//       build the bar; attacks carry the tableau's clicked FOE, use_item
//       carries a clicked ALLY (heal a party member). A sixth SIGNATURE
//       button renders only when archetype has one (every archetype but
//       adventurer): Mend (adept) targets the clicked ALLY like use_item,
//       the other four target the clicked FOE like attack. It greys for the
//       rest of the fight once a send actually lands (Arcane Bolt excepted —
//       at-will, never greys) and un-greys on the next combat.started.
//       Disables only when the frame actually left the machine. When `say`
//       is given, a LAST free-text row renders under the buttons: anything
//       the player types goes to the GM via chat's send+echo path.
//
// The player's combat controls, inside the encounter window under the tableau.
// Each button sends a typed INTENT and nothing else — no numbers, no optimism.
// The server decides everything and the UI only moves when the authoritative
// event comes back, the same contract move/travel/board-accept already follow.
//
// Builds its own DOM rather than querying for markup, so no test or template
// has to be updated to keep the window mountable.

import type { WS } from '../ws.js';
import { ARCHETYPE_INFO, type Archetype } from '../game/archetypes.js';

export interface ActionBarHandle {
  setEnabled(on: boolean): void;
  destroy(): void;
}

export const ACTIONS = [
  { action: 'attack', label: 'Attack', title: 'Strike with your equipped weapon' },
  { action: 'defend', label: 'Defend', title: 'Raise your guard until your next turn' },
  { action: 'dodge', label: 'Dodge', title: 'Give ground — harder to hit once' },
  { action: 'use_item', label: 'Item', title: 'Use something you are carrying' },
  { action: 'flee', label: 'Flee', title: 'Try to break away and end the fight' },
] as const;

export function mountActionBar(
  host: HTMLElement, ws: WS,
  /** The tableau's current clicked FOE, when the window has one. */
  getTargetNpcId: () => number | null = () => null,
  /** The tableau's current clicked ALLY — used only by the Item button. */
  getTargetCharacterId: () => number | null = () => null,
  /** The speaker's own archetype, when known. Drives the sixth signature
   * button's label + target rule; null (or 'adventurer', which carries no
   * signature) renders no sixth button at all. */
  archetype: Archetype | null = null,
  /** The last option: free text the GM interprets — taunts, called shots,
   * anything. Routes through chat's send+echo path (ChatMount.say) so the
   * line lands in the narrative log exactly like typing in chat; the server
   * adjudicates it (say-combat.ts). No row renders when absent. */
  say: ((text: string) => boolean) | null = null,
): ActionBarHandle {
  const bar = document.createElement('div');
  bar.id = 'encounter-actions';

  const hint = document.createElement('span');
  hint.className = 'ew-hint';
  hint.textContent = 'waiting…';

  const buttons = ACTIONS.map(({ action, label, title }) => {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'ew-action';
    b.dataset.action = action;
    b.textContent = label;
    b.title = title;
    b.disabled = true;
    b.addEventListener('click', () => {
      if (b.disabled) return;
      const payload: Record<string, unknown> = { action };
      if (action === 'use_item') {
        // Allies are the Item button's targets (heal a party member); attacks
        // never route through the ally selection.
        const ally = getTargetCharacterId();
        if (ally !== null) payload.target_character_id = ally;
      } else {
        const foe = getTargetNpcId();
        if (foe !== null) payload.target_npc_id = foe;
      }
      // Disable only when the frame actually left the machine: send() reports
      // delivery, and a dead-socket click must not freeze the bar on
      // "waiting…" for a turn that will never advance. The window re-syncs it
      // on system.message / combat.action.resolved for rejections and no-ops.
      if (ws.send('player.combat.action', payload)) setEnabled(false);
    });
    bar.appendChild(b);
    return b;
  });

  // Sixth button: the archetype's signature move (combat/action/signature.ts
  // server-side). Adventurer has none (ARCHETYPE_INFO.adventurer.signature
  // is null). Mend (adept) is the one heal-shaped signature — it targets the
  // clicked ALLY exactly like the Item button; the other four are attack-
  // shaped and target the clicked FOE like Attack.
  const signatureLabel = archetype ? ARCHETYPE_INFO[archetype].signature : null;
  let sigBtn: HTMLButtonElement | null = null;
  // Once-per-combat latch. A send only ever LATCHES on its own resolution
  // (never optimistically), so a refusal — which still arrives as a
  // combat.action.resolved with outcome 'refused', same as every other
  // signature refusal in this codebase — leaves the button live. Arcane
  // Bolt (mage) is at-will server-side (no once-per-combat flag is ever
  // marked for that key), so it is EXCLUDED from the latch entirely: the
  // button only ever tracks turn-availability for that archetype.
  let signatureSpent = false;
  // True from the moment a signature send lands until its own resolution
  // arrives. Combat is strictly turn-based and the whole bar disables the
  // instant the send lands, so the next combat.action.resolved seen while
  // this is true can only be THIS action's own outcome — no actor_key
  // bookkeeping needed to know it's "for self".
  let awaitingSignature = false;
  let onSignatureResolved: ((msg: { outcome: string }) => void) | null = null;
  let onCombatStarted: (() => void) | null = null;

  if (signatureLabel) {
    sigBtn = document.createElement('button');
    sigBtn.type = 'button';
    sigBtn.className = 'ew-action';
    sigBtn.dataset.action = 'signature';
    sigBtn.textContent = signatureLabel;
    sigBtn.title = `Unleash your signature move: ${signatureLabel}`;
    sigBtn.disabled = true;
    sigBtn.addEventListener('click', () => {
      if (sigBtn!.disabled) return;
      const payload: Record<string, unknown> = { action: 'signature' };
      if (archetype === 'adept') {
        const ally = getTargetCharacterId();
        if (ally !== null) payload.target_character_id = ally;
      } else {
        const foe = getTargetNpcId();
        if (foe !== null) payload.target_npc_id = foe;
      }
      if (ws.send('player.combat.action', payload)) {
        awaitingSignature = true;
        setEnabled(false);
      }
    });
    bar.appendChild(sigBtn);

    onSignatureResolved = (msg) => {
      if (!awaitingSignature) return;
      awaitingSignature = false;
      if (msg.outcome !== 'refused' && archetype !== 'mage') signatureSpent = true;
      syncSigButton();
    };
    // A fresh fight clears the once-per-combat latch — mirrors the target
    // reset the encounter window itself runs on every combat.started.
    onCombatStarted = () => {
      signatureSpent = false;
      awaitingSignature = false;
      syncSigButton();
    };
    ws.on('combat.action.resolved', onSignatureResolved);
    ws.on('combat.started', onCombatStarted);
  }

  bar.appendChild(hint);

  // The last option: type anything. Unlike the buttons this never disables
  // the bar on send — a 'talk' verdict runs a full GM turn that may not
  // advance the turn, and the chat input it mirrors stays live for exactly
  // that reason. The server's combat gate + say rate limit own the rest.
  let sayInput: HTMLInputElement | null = null;
  let saySend: HTMLButtonElement | null = null;
  if (say) {
    const sayForm = document.createElement('form');
    sayForm.className = 'ew-say';
    sayInput = document.createElement('input');
    sayInput.type = 'text';
    sayInput.className = 'ew-say-input';
    sayInput.placeholder = 'Or describe anything — a taunt, a called shot, a wild idea…';
    sayInput.autocomplete = 'off';
    sayInput.disabled = true;
    saySend = document.createElement('button');
    saySend.type = 'submit';
    saySend.className = 'ew-action ew-say-send';
    saySend.textContent = 'Do it';
    saySend.title = 'Describe your move in your own words — the GM decides what happens';
    saySend.disabled = true;
    sayForm.addEventListener('submit', (e) => {
      e.preventDefault();
      if (sayInput!.disabled) return;
      const text = sayInput!.value.trim();
      if (!text) return;
      // say() echoes to the log and reports delivery; a failed send keeps
      // the text in the box so retry is one Enter away.
      if (say(text)) sayInput!.value = '';
    });
    sayForm.appendChild(sayInput);
    sayForm.appendChild(saySend);
    bar.appendChild(sayForm);
  }

  host.appendChild(bar);

  let barOn = false;
  function syncSigButton(): void {
    if (sigBtn) sigBtn.disabled = !barOn || signatureSpent;
  }

  function setEnabled(on: boolean): void {
    barOn = on;
    for (const b of buttons) b.disabled = !on;
    if (sayInput) sayInput.disabled = !on;
    if (saySend) saySend.disabled = !on;
    syncSigButton();
    hint.textContent = on ? 'your move' : 'waiting…';
    bar.classList.toggle('ew-actions-live', on);
  }

  return {
    setEnabled,
    destroy: () => {
      if (onSignatureResolved) ws.off('combat.action.resolved', onSignatureResolved);
      if (onCombatStarted) ws.off('combat.started', onCombatStarted);
      bar.remove();
    },
  };
}
